import moment from 'moment';
import { isMongooseObjectId } from '../common/functions';
import type { IdKey } from './types/common';
import type { Vertex, Collection } from './interfaces/vertices/util';
import type { DateType } from '../common/types';
import type { Interval } from './interfaces';
import type { IDType, OmitInternalVertexKeys, PrivateIDType } from './interfaces/vertices/util/vertex';

/**
 * Returns the ID key to use for vertices, which can be either `id`, `_id`, or `ids`.
 */
export function getIdKey ({ idKey }: { idKey?: IdKey }) {
  return idKey ?? 'id';
}

/**
 * Returns the ID of a vertex, which can be either `_id`, `id`, or `ids`.
 */
export function getVertexId (
  edge:   IDType | PrivateIDType | Partial<OmitInternalVertexKeys<Vertex<Collection>>>,
  _idKey: IdKey | { idKey?: IdKey }
): string {
  if (typeof edge === 'string') return edge;
  if (isMongooseObjectId(edge)) return edge.toString();

  // the edge must be an object at this point
  if (typeof edge !== 'object' || edge === null) {
    throw new Error(`Unable to get vertex ID from edge: ${JSON.stringify(edge)}`);
  }

  const idKey = typeof _idKey === 'string' ? _idKey : getIdKey(_idKey);
  if (idKey == '_id' && idKey in edge && edge[idKey]) return edge[idKey].toString();
  if (idKey == 'id'  && idKey in edge && edge[idKey]) return edge[idKey];
  if (idKey == 'ids' && idKey in edge && edge[idKey]) return edge[idKey];

  throw new Error(`Unable to get vertex ID "${idKey}" from edge: ${JSON.stringify(edge)}`);
}


/**
 * Returns the day index in the range 0-6, where 0 is Monday and 6 is Sunday.
 */
export function getDayIndex (value: DateType | number): number {
  const day = typeof value === 'number' ? value : moment.utc(value).day();
  return (day + 6) % 7;
}

export function parseDateInterval (interval: Partial<Interval<DateType>>, type?: string): Interval<moment.Moment> {
  if (!interval.start) throw new Error(`(RS::To::validateInterval) The interval ${type ? `"${type}"` : ''} is missing a start time`);
  if (!interval.end  ) throw new Error(`(RS::To::validateInterval) The interval ${type ? `"${type}"` : ''} is missing an end time`);

  const start = moment.utc(interval.start);
  const end   = moment.utc(interval.end);
  if (!start.isValid()) throw new Error(`(RS::To::validateInterval) The interval ${type ? `"${type}"` : ''} has an invalid start time`);
  if (!end  .isValid()) throw new Error(`(RS::To::validateInterval) The interval ${type ? `"${type}"` : ''} has an invalid end time`);

  if (end.isSameOrBefore(start)) throw new Error(`(RS::To::validateInterval) The interval ${type ? `"${type}"` : ''} is invalid: end time is before or equal to start time`);

  return { start, end };
}